/**
 * Team permission model — ordered per-area access LEVELS.
 *
 * Overhaul decisions baked in here (see docs in the PR description / report):
 *  1. Delete is its own opt-in level (`full`), never bundled into `edit`.
 *     Ordered levels per area: None · View · Edit · Full.
 *       - `view`  → read-only (only areas with a dedicated view flag expose it)
 *       - `edit`  → manage + start/stop toggle (folds into Edit)
 *       - `full`  → edit + delete
 *     Areas that are genuinely binary (Paywall, Multiview, Team, Developers,
 *     Account) render as a single on/off switch instead of the 4-level control.
 *  2. Flags that don't fit the ordered ladder (destination enable/disable) live
 *     in a per-row "Advanced" expander rather than inventing a new level.
 *  3. Default invite role is Moderator with a conservative least-privilege preset.
 *
 * IMPORTANT: only areas the backend actually supports today are listed. We do
 * NOT add rows for Transcoder / Destinations / Playlists / Dynamic / TV Playout /
 * Sub-Second even though they exist as products — the team permissions API has no
 * flags for them yet. See report "Backend gaps".
 */

// Fallback list — only used when the plan definition override keys are absent.
// Prefer `definition.teamSpecificStreamSharing` / `teamSpecificVodSharing` /
// `teamInviteUsers`; these IDs are the legacy hard-coded allow-list.
const TEAM_SHARING_PLAN_IDS = [
	'643502dd40771d569f1e98f0', // Ultra Monthly
	'6435044340771d569f1e98fa', // Ultra Yearly
	'698d84497375f3f790ec54b5',
	'698d845b7375f3f790ec54b6',
	'698d83927375f3f790ec54b1',
	'698d839e7375f3f790ec54b2',
]

export const LEVELS = ['none', 'view', 'edit', 'full']
export const LEVEL_LABELS = {
	none: 'None',
	view: 'View',
	edit: 'Edit',
	full: 'Full',
}

export const TEAM_PERMISSION_SECTIONS = [
	{ id: 'stream', title: 'Streaming' },
	{ id: 'content', title: 'Content & Monetization' },
	{ id: 'advanced', title: 'Advanced' },
	{ id: 'account', title: 'Account' },
]

/**
 * Product rows. `kind`:
 *  - 'levels': ordered ladder. `levels` lists the rungs this area offers.
 *      viewFlags → set at `view`+, editFlags → `edit`+, deleteFlags → `full`.
 *  - 'switch': binary on/off. `onFlags` toggle together.
 * `advanced`: extra flags surfaced under an Advanced expander (edit+ only).
 * `scope` / `accessKey`: content-scoping (live streams / vod folders).
 */
export const TEAM_PERMISSION_ROWS = [
	{
		id: 'live',
		name: 'live',
		label: 'Live Stream',
		section: 'stream',
		description: 'Go live, manage sources, and control your streams.',
		kind: 'levels',
		levels: ['none', 'view', 'edit', 'full'],
		viewFlags: ['liveStreamView'],
		editFlags: ['liveStreamManage'],
		deleteFlags: ['liveStreamDelete'],
		advanced: [
			{ flag: 'liveStreamToggle', label: 'Enable or disable the stream' },
			{ flag: 'liveStreamPlatformToggle', label: 'Enable or disable destinations' },
		],
		scope: 'live',
		accessKey: 'liveStreamAccessIds',
	},
	{
		id: 'restream',
		name: 'restream',
		label: 'Multistream',
		section: 'stream',
		description: 'Restream a single source to many destinations.',
		kind: 'levels',
		levels: ['none', 'edit', 'full'],
		viewFlags: [],
		editFlags: ['recordedStreamManage'],
		deleteFlags: ['recordedStreamDelete'],
		advanced: [
			{ flag: 'recordedStreamToggle', label: 'Enable or disable the stream' },
			{ flag: 'recordedStreamPlatformToggle', label: 'Enable or disable destinations' },
		],
	},
	{
		id: 'scheduled',
		name: 'scheduled',
		label: 'Pre-recorded Stream',
		section: 'stream',
		description: 'Schedule and manage looped or pre-recorded streams.',
		kind: 'levels',
		levels: ['none', 'edit', 'full'],
		viewFlags: [],
		editFlags: ['preRecordedStreamManage'],
		deleteFlags: ['preRecordedStreamDelete'],
		advanced: [
			{ flag: 'preRecordedStreamToggle', label: 'Enable or disable the stream' },
			{ flag: 'preRecordedStreamPlatformToggle', label: 'Enable or disable destinations' },
		],
	},
	{
		id: 'maxVodItems',
		name: 'maxVodItems',
		label: 'Content Library',
		section: 'content',
		description: 'Upload, organize, and share hosted videos.',
		kind: 'levels',
		levels: ['none', 'edit', 'full'],
		viewFlags: [],
		editFlags: ['hostingManage'],
		deleteFlags: ['hostingDelete'],
		scope: 'vod',
		accessKey: 'vodStreamAccessIds',
	},
	{
		id: 'paywall',
		name: 'paywall',
		label: 'Paywall',
		section: 'content',
		description: 'Manage paywalls and monetization.',
		kind: 'switch',
		onFlags: ['paywall'],
	},
	{
		id: 'sites',
		name: 'sites',
		label: 'Sites',
		section: 'content',
		description: 'Create and manage hosted OTT sites.',
		kind: 'switch',
		onFlags: ['sites'],
	},
	{
		id: 'multiview',
		name: 'multiview',
		label: 'Multiview',
		section: 'advanced',
		description: 'Create and manage multiview layouts.',
		kind: 'switch',
		onFlags: ['multiview'],
	},
	{
		id: 'team',
		name: 'team',
		label: 'Team',
		section: 'account',
		description: 'View the team page and its members.',
		kind: 'switch',
		onFlags: ['teamView'],
	},
	{
		id: 'developers',
		name: 'developers',
		label: 'Developers',
		section: 'account',
		description: 'Access developer and API settings.',
		kind: 'switch',
		onFlags: ['developers'],
	},
	{
		id: 'account',
		name: 'account',
		label: 'Account',
		section: 'account',
		description: 'View account settings (read-only).',
		kind: 'switch',
		onFlags: ['accountView'],
	},
]

/** Every flag a row can touch (view + edit + delete + advanced). */
function rowAllFlags(row) {
	if (row.kind === 'switch') return [...(row.onFlags || [])]
	const flags = [
		...(row.viewFlags || []),
		...(row.editFlags || []),
		...(row.deleteFlags || []),
	]
	for (const adv of row.advanced || []) flags.push(adv.flag)
	return [...new Set(flags)]
}

export function createDefaultPermissions(role = 'moderator') {
	const base = {
		role,
		isEnabled: true,
		liveStreamView: false,
		liveStreamManage: false,
		liveStreamDelete: false,
		liveStreamToggle: false,
		liveStreamPlatformToggle: false,
		// Narrows `liveStreamView` to the livestream Chat tab's Live room. NOT a
		// rung on the None/View/Edit/Full ladder — see `chatOnlyApplies`.
		liveStreamChatModerationOnly: false,
		recordedStreamManage: false,
		recordedStreamDelete: false,
		recordedStreamToggle: false,
		recordedStreamPlatformToggle: false,
		preRecordedStreamManage: false,
		preRecordedStreamDelete: false,
		preRecordedStreamToggle: false,
		preRecordedStreamPlatformToggle: false,
		hostingManage: false,
		hostingDelete: false,
		paywall: false,
		developers: false,
		refer: false,
		teamView: false,
		accountView: false,
		subAccountInfoCreate: false,
		subAccountInfoView: false,
		subAccountStreamView: false,
		multiview: false,
		teamInviteUsers: false,
		liveStreamAccessIds: [],
		vodStreamAccessIds: [],
	}
	if (role === 'admin') return createAdminPermissions()
	return applyModeratorPreset(base)
}

/** Admin = everything on (the backend also force-enables this server-side). */
export function createAdminPermissions() {
	const perms = createDefaultPermissions('__seed__')
	for (const key of Object.keys(perms)) {
		if (typeof perms[key] === 'boolean') perms[key] = true
	}
	perms.role = 'admin'
	perms.isEnabled = true
	// Admins are never chat-restricted; the blanket `true` loop above would
	// otherwise invert this flag's meaning.
	perms.liveStreamChatModerationOnly = false
	perms.liveStreamAccessIds = []
	perms.vodStreamAccessIds = []
	return perms
}

/**
 * Conservative least-privilege moderator preset: can VIEW live streams, nothing
 * else, all content scoped to "All". Never defaults to broad access.
 */
export function applyModeratorPreset(permissions) {
	let next = { ...(permissions || {}) }
	next.role = 'moderator'
	next.isEnabled = next.isEnabled !== false
	// Reset every product flag to false first.
	for (const row of TEAM_PERMISSION_ROWS) {
		next = applyRowLevel(next, row, 'none')
	}
	next.teamInviteUsers = false
	// Give it one sensible read-only rung so the invite isn't "no access".
	next = applyRowLevel(next, TEAM_PERMISSION_ROWS.find((r) => r.id === 'live'), 'view')
	next.liveStreamAccessIds = []
	next.vodStreamAccessIds = []
	return next
}

export function createModeratorPreset() {
	return applyModeratorPreset(createDefaultPermissions('__seed__'))
}

/** Highest level currently expressed by `permissions` for a levels-row. */
export function levelFromPermissions(permissions, row) {
	if (!permissions || !row) return 'none'
	if (row.kind === 'switch') {
		return (row.onFlags || []).some((f) => !!permissions[f]) ? 'full' : 'none'
	}
	const has = (flags) => (flags || []).some((f) => !!permissions[f])
	if (row.levels.includes('full') && has(row.deleteFlags)) return 'full'
	if (row.levels.includes('edit') && has(row.editFlags)) return 'edit'
	if (row.levels.includes('view') && has(row.viewFlags)) return 'view'
	return 'none'
}

/** Numeric rank of a level within LEVELS (for comparisons). */
export function levelRank(level) {
	const i = LEVELS.indexOf(level)
	return i < 0 ? 0 : i
}

/**
 * Return a new permissions object with `row` set to `level`.
 * Advanced flags are cleared below `edit`, where they stop being rendered;
 * between edit and full they are left untouched so explicit choices survive.
 */
export function applyRowLevel(permissions, row, level) {
	const next = { ...(permissions || {}) }
	if (!row) return next
	if (row.kind === 'switch') {
		const on = level !== 'none'
		for (const flag of row.onFlags || []) next[flag] = on
		return next
	}
	const target = levelRank(level)
	for (const flag of row.viewFlags || []) next[flag] = target >= levelRank('view')
	for (const flag of row.editFlags || []) next[flag] = target >= levelRank('edit')
	for (const flag of row.deleteFlags || []) next[flag] = target >= levelRank('full')
	// Chat-only moderation only means anything at `view`: None removes access
	// entirely, and Edit/Full contradict the restriction.
	if (row.id === 'live' && level !== 'view') next.liveStreamChatModerationOnly = false
	// Advanced flags render at edit/full only, so below that they would stay set
	// with nothing on screen saying so — and the server still honours them.
	if (target < levelRank('edit')) {
		for (const adv of row.advanced || []) next[adv.flag] = false
	}
	if (level === 'none' && row.accessKey) next[row.accessKey] = []
	return next
}

/** Toggle a single Advanced flag (destination enable/disable, etc.). */
export function setAdvancedFlag(permissions, flag, value) {
	return { ...(permissions || {}), [flag]: !!value }
}

/**
 * True when the "Moderate Chat only" toggle should be offered for this row/level.
 * It narrows `liveStreamView`, so it is meaningless anywhere else on the ladder —
 * which is also why it can't live in the row's `advanced` array (that renders at
 * edit/full only, see TeamPermissionRow.vue).
 */
export function chatOnlyApplies(row, level) {
	return row?.id === 'live' && level === 'view'
}

export function setChatModerationOnly(permissions, value) {
	return { ...(permissions || {}), liveStreamChatModerationOnly: !!value }
}

export function rowIsBinary(row) {
	return row?.kind === 'switch'
}

export function rowLevels(row) {
	if (!row || row.kind === 'switch') return ['none', 'full']
	return row.levels || LEVELS
}

/* ------------------------------------------------------------------ *
 * Entitlements (prefer definition override keys; plan-ID list = fallback)
 * ------------------------------------------------------------------ */

function packageAllowsTeamSharing(item) {
	if (TEAM_SHARING_PLAN_IDS.includes(item?.package?._id)) return true
	const name = String(item?.package?.name || '').toLowerCase()
	return /\bultra\b/.test(name) || /\bevents\b/.test(name)
}

function planAllowsSharing(subscriptions, overrideKey) {
	const list = Array.isArray(subscriptions) ? subscriptions : []
	return list.some((item) => {
		if (item?.definitionOverride?.[overrideKey]) return true
		if (item?.package?.definition?.[overrideKey]) return true
		return packageAllowsTeamSharing(item)
	})
}

export function hasTeamStreamSharing(subscriptions) {
	return planAllowsSharing(subscriptions, 'teamSpecificStreamSharing')
}

export function hasTeamVodSharing(subscriptions) {
	return planAllowsSharing(subscriptions, 'teamSpecificVodSharing')
}

export function canUseInviteOthersFeature(addonSubscriptions) {
	try {
		const liveSub = (addonSubscriptions || []).find((s) => s?.category === 'live')
		if (!liveSub) return false
		return !!(
			liveSub.definitionOverride?.teamInviteUsers ??
			liveSub.package?.definition?.teamInviteUsers
		)
	} catch {
		return false
	}
}

export function inviteOthersTooltip(addonSubscriptions) {
	const hasContactSales = (addonSubscriptions || []).some((s) => {
		if (s?.category !== 'live') return false
		const cycleAge = s?.package?.cycleAge
		const baseCharge = s?.package?.baseCharge
		return (baseCharge > 3000 && cycleAge === 365) || (baseCharge > 200 && cycleAge === 30)
	})
	return hasContactSales
		? 'Contact sales to enable this setting.'
		: 'Upgrade to the Ultra plan to enable this setting.'
}

export function scopeLockMessage(scope) {
	if (scope === 'live') return 'Selecting specific live streams is available on the Ultra plan.'
	if (scope === 'vod') return 'Selecting specific folders is available on the Ultra plan.'
	return 'Selecting specific items is available on the Ultra plan.'
}

/* ------------------------------------------------------------------ *
 * Visible-rows / entitlement gating
 * ------------------------------------------------------------------ */

export function allowedPermissionCategories(baseSubscription, addonSubscriptions) {
	const categories = ['others', 'paywall', 'team', 'developers', 'referAndEarn', 'account', 'multiview']

	const pushCat = (packCat, vod) => {
		if (packCat && !categories.includes(packCat)) categories.push(packCat)
		if (vod > 0 && !categories.includes('maxVodItems')) categories.push('maxVodItems')
	}

	if (baseSubscription?.enabled) {
		pushCat(baseSubscription.package?.category, baseSubscription.package?.definition?.maxVodItems)
	}

	for (const single of addonSubscriptions || []) {
		if (!single?.enabled) continue
		const packCat = single.package?.category
		pushCat(packCat, single.package?.definition?.maxVodItems)
		if (packCat === 'live') {
			// NOTE: intentionally NOT adding transcoder / tvPlayout / playlist /
			// subSecond / dynamicPlayer rows — no backend team-permission flags yet.
			categories.push('restream', 'scheduled')
		}
	}

	return categories
}

/** live/restream/scheduled all key off the `live` category being present. */
function categoryForRow(row) {
	if (row.name === 'restream' || row.name === 'scheduled') return 'live'
	return row.name
}

export function visiblePermissionRows(baseSubscription, addonSubscriptions) {
	const categories = allowedPermissionCategories(baseSubscription, addonSubscriptions)
	return TEAM_PERMISSION_ROWS.filter((row) => categories.includes(categoryForRow(row)))
}

export function groupedPermissionRows(rows) {
	return TEAM_PERMISSION_SECTIONS
		.map((section) => ({
			...section,
			rows: (rows || []).filter((row) => row.section === section.id),
		}))
		.filter((section) => section.rows.length > 0)
}

export function areAllProductRowsOff(permissions, rows) {
	if (!rows?.length) return false
	return rows.every((row) => levelFromPermissions(permissions, row) === 'none')
}

/** True when any flag for that product row is on (view/edit/full). */
export function hasProductAccess(permissions, rowId) {
	if (!permissions) return false
	const row = TEAM_PERMISSION_ROWS.find((r) => r.id === rowId)
	if (!row) return false
	return levelFromPermissions(permissions, row) !== 'none'
}

/* ------------------------------------------------------------------ *
 * Scope semantics — "All" is explicit; "Specific" requires >= 1.
 * We persist scope in the same backend field (empty ids = all), but the UI
 * forces an explicit All/Specific choice and blocks empty-Specific.
 * ------------------------------------------------------------------ */

/** Derive the UI scope mode from stored ids. Empty ids = "all". */
export function scopeModeFromIds(ids) {
	return Array.isArray(ids) && ids.length > 0 ? 'specific' : 'all'
}

/** True when a "Specific" choice has no items selected (invalid state). */
export function scopeIsInvalid(mode, ids) {
	return mode === 'specific' && (!Array.isArray(ids) || ids.length === 0)
}

export function scopeSummaryLabel(mode, selectedCount, itemCount) {
	if (mode === 'all') return itemCount ? `All ${itemCount}` : 'All'
	if (!selectedCount) return 'Specific'
	return `${selectedCount} of ${itemCount}`
}

/**
 * Normalize a permissions object before submit:
 *  - flags for rows that are hidden/unentitled are forced to false;
 *  - scope ids are cleared when the plan can't scope;
 *  - "all" scope collapses to empty ids (backend "empty = all").
 * `scopeModes` is a map { live, vod } of 'all' | 'specific'.
 */
export function normalizePermissions(permissions, {
	visibleRows,
	canPickStreams = false,
	canPickVods = false,
	scopeModes = {},
} = {}) {
	const next = { ...(permissions || {}) }
	const visible = new Set((visibleRows || TEAM_PERMISSION_ROWS).map((r) => r.id))
	for (const row of TEAM_PERMISSION_ROWS) {
		if (visible.has(row.id)) continue
		for (const flag of rowAllFlags(row)) next[flag] = false
		if (row.accessKey) next[row.accessKey] = []
	}
	// Advanced flags only make sense once the member can at least edit the
	// stream, which is also the point they start rendering. Submit-time rather
	// than UI-time, so a stale or hand-rolled payload can't carry one through at
	// View with no control on screen to show it.
	for (const row of TEAM_PERMISSION_ROWS) {
		if (row.kind !== 'levels') continue
		if (levelRank(levelFromPermissions(next, row)) < levelRank('edit')) {
			for (const adv of row.advanced || []) next[adv.flag] = false
		}
	}
	// Submit-time last line of defence for chat-only moderation. The flag is not
	// in any row's flag lists, so the hidden-row loop above never touches it and
	// this must not rely on the UI having cleared it.
	const liveRow = TEAM_PERMISSION_ROWS.find((r) => r.id === 'live')
	if (!visible.has('live') || !chatOnlyApplies(liveRow, levelFromPermissions(next, liveRow))) {
		next.liveStreamChatModerationOnly = false
	}
	if (!canPickStreams) next.liveStreamAccessIds = []
	if (!canPickVods) next.vodStreamAccessIds = []
	if (scopeModes.live === 'all') next.liveStreamAccessIds = []
	if (scopeModes.vod === 'all') next.vodStreamAccessIds = []
	return next
}

/* ------------------------------------------------------------------ *
 * Seat math — count active + pending toward the limit; owner is not a seat.
 * paused/disabled members still occupy a seat.
 *
 * `elsewhere` carries the seats held by the account's other workspaces. The
 * limit is the subscription's and every workspace draws on it, so a workspace
 * that looks empty can still be out of seats.
 * ------------------------------------------------------------------ */

export function computeSeatUsage(members, max, elsewhere = 0) {
	const list = Array.isArray(members) ? members : []
	const isOwner = (m) => String(m?.role || '').toLowerCase() === 'owner'
	const seats = list.filter((m) => !isOwner(m) && m.status !== 'pending')
	const pending = list.filter((m) => m.status === 'pending')
	const disabled = seats.filter((m) => m.enabled === false)
	const used = seats.length
	const pendingCount = pending.length
	const otherProjects = Math.max(Number(elsewhere) || 0, 0)
	const occupied = used + pendingCount + otherProjects
	const limit = Number(max) || 0
	return {
		used,
		pending: pendingCount,
		disabled: disabled.length,
		otherProjects,
		occupied,
		max: limit,
		// Off-by-one fix: at the limit means occupied >= max, not >.
		atLimit: limit > 0 && occupied >= limit,
	}
}
